/**
 * Chuông thông báo trên header (plan 2026-10-03 §1) — badge số chưa đọc (poll 60s),
 * bấm mở danh sách 8 thông báo gần nhất; bấm 1 thông báo → đánh dấu đã đọc + mở link.
 * Chỉ hiện khi backend bật `NOTIFICATIONS_ENABLED` (`user.feature_flags.notifications`).
 */
import { useState } from "react";
import { Badge, Button, Empty, List, Popover, Space, Spin, Tag, Typography } from "antd";
import { BellOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import "dayjs/locale/vi";
import {
  type AppNotification,
  useMarkAllRead,
  useNotifications,
  useUnreadCount,
} from "@/api/notifications";
import { CATEGORY_COLORS, useOpenNotification } from "@/components/notificationHelpers";
import { useAuthStore } from "@/stores/auth";

dayjs.extend(relativeTime);

const { Text } = Typography;

export function NotificationItem({
  item,
  onOpen,
}: {
  item: AppNotification;
  onOpen: (item: AppNotification) => void;
}) {
  return (
    <List.Item
      onClick={() => onOpen(item)}
      style={{
        cursor: "pointer",
        padding: "10px 12px",
        background: item.read_at ? undefined : "#e6f4ff",
        borderRadius: 6,
      }}
    >
      <Space direction="vertical" size={2} style={{ width: "100%" }}>
        <Space size={6} wrap>
          <Tag color={CATEGORY_COLORS[item.category]} style={{ marginInlineEnd: 0 }}>
            {item.category_label}
          </Tag>
          <Text type="secondary" style={{ fontSize: 12 }}>
            {dayjs(item.created_at).locale("vi").fromNow()}
          </Text>
        </Space>
        <Text strong={!item.read_at}>{item.title}</Text>
        {item.body && (
          <Text type="secondary" style={{ fontSize: 13 }} ellipsis={{ tooltip: item.body }}>
            {item.body}
          </Text>
        )}
      </Space>
    </List.Item>
  );
}

export function NotificationBell({ dark = false }: { dark?: boolean }) {
  const user = useAuthStore((s) => s.user);
  const enabled = !!user?.feature_flags?.notifications && !!user?.tenant;
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const unread = useUnreadCount(enabled);
  const list = useNotifications({ limit: 8 }, enabled && open);
  const markAll = useMarkAllRead();
  const openItem = useOpenNotification();

  if (!enabled) return null;

  const content = (
    <div style={{ width: 360 }}>
      <Space style={{ width: "100%", justifyContent: "space-between", marginBottom: 8 }}>
        <Text strong>Thông báo</Text>
        <Button
          size="small"
          type="link"
          disabled={!unread.data}
          loading={markAll.isPending}
          onClick={() => markAll.mutate()}
        >
          Đánh dấu đã đọc hết
        </Button>
      </Space>
      {list.isLoading ? (
        <div style={{ textAlign: "center", padding: 24 }}>
          <Spin />
        </div>
      ) : list.data?.results.length ? (
        <List
          dataSource={list.data.results}
          split={false}
          style={{ maxHeight: 420, overflowY: "auto" }}
          renderItem={(item) => (
            <NotificationItem
              item={item}
              onOpen={(n) => {
                setOpen(false);
                openItem(n);
              }}
            />
          )}
        />
      ) : (
        <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Chưa có thông báo" />
      )}
      <div style={{ textAlign: "center", borderTop: "1px solid #f0f0f0", paddingTop: 8 }}>
        <Button
          type="link"
          onClick={() => {
            setOpen(false);
            navigate("/notifications");
          }}
        >
          Xem tất cả & cài đặt
        </Button>
      </div>
    </div>
  );

  return (
    <Popover
      trigger="click"
      placement="bottomRight"
      open={open}
      onOpenChange={setOpen}
      content={content}
      arrow={false}
    >
      <Badge count={unread.data ?? 0} size="small" offset={[-4, 4]}>
        <Button
          type="text"
          aria-label="Thông báo"
          icon={<BellOutlined style={{ fontSize: 18, color: dark ? "#fff" : undefined }} />}
        />
      </Badge>
    </Popover>
  );
}
